import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProductTrioTaskCreationRoleStatusEnum } from 'src/app/enums/product-trio-task-creation-role-status.enum';
import { ProductionKeyPerformanceIndicatorEvaluationWithData } from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { ProductTrioTaskCreationKpiData, ProductTrioTaskCreationRoleItem } from 'src/app/models/product-trio-task-creation-kpi-data.model';
import { UserProfilePictureComponent } from 'src/app/components/utils/user-profile-picture/user-profile-picture.component';

interface RoleUserRow {
  id: number;
  full_name: string;
  pokemon_profile_picture_url: string;
  serious_profile_picture_url: string;
}

interface RoleRow {
  role_slug: string;
  role_label: string;
  users: RoleUserRow[];
  has_assigned_member: boolean;
  created_tasks_count: number;
  created_tasks_prcnt: number | null;
  fill_width_prcnt: number;
  status: ProductTrioTaskCreationRoleStatusEnum;
  status_color: string;
  status_label: string;
  icon: string;
}

const ROLE_ICONS: Record<string, string> = {
  'product-manager': 'campaign',
  'tech-lead': 'engineering',
  'product-designer': 'palette',
};

const FALLBACK_ROLE_ICON = 'person';

const DEFAULT_PROFILE_PICTURE = 'assets/img/default_profile_picture.png';

@Component({
  selector: 'app-product-trio-task-creation-details',
  imports: [CommonModule, DecimalPipe, MatIconModule, MatTooltipModule, UserProfilePictureComponent],
  templateUrl: './product-trio-task-creation-details.component.html',
  styleUrl: './product-trio-task-creation-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTrioTaskCreationDetailsComponent {
  readonly defaultProfilePicture = DEFAULT_PROFILE_PICTURE;

  kpiData = input.required<ProductionKeyPerformanceIndicatorEvaluationWithData>();
  optimalThreshold = input.required<number>();
  acceptableThreshold = input.required<number>();

  private detailsData = computed((): ProductTrioTaskCreationKpiData | null => {
    const raw = this.kpiData().data?.[0]?.data;
    if (ProductTrioTaskCreationDetailsComponent.isProductTrioTaskCreationKpiData(raw)) {
      return raw;
    }

    if (raw === null || typeof raw !== 'object') {
      return null;
    }

    const nestedData = (raw as { company_product_general_kpi?: { kpi_data?: unknown } }).company_product_general_kpi?.kpi_data;
    return ProductTrioTaskCreationDetailsComponent.isProductTrioTaskCreationKpiData(nestedData) ? nestedData : null;
  });

  totalNewTasks = computed((): number => this.detailsData()?.total_new_tasks ?? 0);
  trioCreatedTasksCount = computed((): number => this.detailsData()?.trio_created_tasks_count ?? 0);
  minRolePrcnt = computed((): number | null => this.detailsData()?.min_role_prcnt ?? null);

  trioCoveragePrcnt = computed((): number | null => {
    const total = this.totalNewTasks();
    if (total <= 0) {
      return null;
    }
    return (this.trioCreatedTasksCount() / total) * 100;
  });

  roles = computed((): RoleRow[] => {
    const data = this.detailsData();
    if (!data) {
      return [];
    }

    const total = data.total_new_tasks;
    return data.roles.map((role): RoleRow => this.buildRoleRow(role, total));
  });

  private buildRoleRow(role: ProductTrioTaskCreationRoleItem, totalNewTasks: number): RoleRow {
    const status = this.resolveRoleStatus(role, totalNewTasks);
    const statusStyle = this.resolveStatusStyle(status);
    const users = role.users.map(
      (user): RoleUserRow => ({
        id: user.id,
        full_name: user.full_name,
        pokemon_profile_picture_url: user.pokemon_profile_picture_url ?? DEFAULT_PROFILE_PICTURE,
        serious_profile_picture_url: user.serious_profile_picture_url ?? DEFAULT_PROFILE_PICTURE,
      })
    );
    const fillWidth = this.resolveFillWidth(role.created_tasks_prcnt);

    return {
      role_slug: role.role_slug,
      role_label: role.role_label,
      users,
      has_assigned_member: role.has_assigned_member ?? role.users.length > 0,
      created_tasks_count: role.created_tasks_count,
      created_tasks_prcnt: role.created_tasks_prcnt,
      fill_width_prcnt: fillWidth,
      status,
      status_color: statusStyle.color,
      status_label: statusStyle.label,
      icon: ROLE_ICONS[role.role_slug] ?? FALLBACK_ROLE_ICON,
    };
  }

  private resolveRoleStatus(role: ProductTrioTaskCreationRoleItem, totalNewTasks: number): ProductTrioTaskCreationRoleStatusEnum {
    if (!role.has_assigned_member) {
      return ProductTrioTaskCreationRoleStatusEnum.UNASSIGNED;
    }
    if (totalNewTasks <= 0 || role.created_tasks_prcnt === null) {
      return ProductTrioTaskCreationRoleStatusEnum.NO_TASKS;
    }
    if (role.created_tasks_prcnt >= this.optimalThreshold()) {
      return ProductTrioTaskCreationRoleStatusEnum.GOOD;
    }
    if (role.created_tasks_prcnt >= this.acceptableThreshold()) {
      return ProductTrioTaskCreationRoleStatusEnum.MEDIUM;
    }
    return ProductTrioTaskCreationRoleStatusEnum.POOR;
  }

  private resolveStatusStyle(status: ProductTrioTaskCreationRoleStatusEnum): { color: string; label: string } {
    switch (status) {
      case ProductTrioTaskCreationRoleStatusEnum.GOOD:
        return { color: 'var(--green)', label: 'Excelente' };
      case ProductTrioTaskCreationRoleStatusEnum.MEDIUM:
        return { color: 'var(--yellow)', label: 'Aceptable' };
      case ProductTrioTaskCreationRoleStatusEnum.POOR:
        return { color: 'var(--warn-color)', label: 'Mejorable' };
      case ProductTrioTaskCreationRoleStatusEnum.UNASSIGNED:
        return { color: 'var(--grey_text_color)', label: 'Sin asignar' };
      case ProductTrioTaskCreationRoleStatusEnum.NO_TASKS:
        return { color: 'var(--grey_text_color)', label: 'Sin tareas' };
    }
  }

  private resolveFillWidth(prcnt: number | null): number {
    if (prcnt === null) {
      return 0;
    }
    // Tope visual al 100% para que el % siempre se vea proporcional al área disponible.
    return Math.max(0, Math.min(100, prcnt));
  }

  public static isProductTrioTaskCreationKpiData(value: unknown): value is ProductTrioTaskCreationKpiData {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const partial = value as Partial<ProductTrioTaskCreationKpiData>;
    return (
      typeof partial.total_new_tasks === 'number' && typeof partial.trio_created_tasks_count === 'number' && Array.isArray(partial.roles)
    );
  }
}
